import { Evidence } from '../types/types';
import { normalizeEntity, getUniqueCanonicalEntities } from './entityClustering';

// Note: SharePoint links in the data are document names only
// They would need the full SharePoint site URL and document library path to be functional
// For now, we'll store them as-is for reference
function processSharePointLink(filename: string | null | undefined): string | null {
  if (!filename) return null;
  // If it's already a full URL, return as is
  if (filename.startsWith('http://') || filename.startsWith('https://')) {
    return filename;
  }
  // Return the filename as-is - it's just a reference to the document name
  return filename;
}

// Normalize entities to their canonical forms
function normalizeEntities(entities: string[]): string[] {
  if (!entities || entities.length === 0) return [];
  
  // Get unique canonical entities (this also deduplicates)
  return getUniqueCanonicalEntities(entities);
}

interface ParallelEvidence {
  id: string;
  description: string;
  shift_type: string;
  implementation_stage?: {
    status: string;
    progress_description: string;
  };
  evidence_nature?: string;
  source_reference?: {
    section: string;
    key_quote: string;
  };
  data_gaps?: {
    critical_missing?: string[];
    affects_confidence?: boolean;
  };
  requires_clarification?: {
    ambiguous_elements?: string[];
    verification_needed?: boolean;
  };
  status?: string;
  entities: string[];
  locations?: string[];
  reporting_period?: string;
  sharepoint_link?: string;
  confidence?: {
    score: number;
    rationale: string;
  } | null;
  accuracy_notes?: {
    source_language?: string[];
    interpretation_caveats?: string[];
    missing_context?: string[];
  };
  context?: {
    background?: string;
    immediate_impact?: string;
    future_implications?: string;
    stakeholders?: string[];
    systems_affected?: string;
  };
  supporting_details?: {
    quotes?: string[];
    metrics?: string[];
    timeline?: string;
    conditions?: string;
    related_initiatives?: string[];
  };
  source_document?: {
    title: string;
    date: string;
    source: string;
  };
}

export function adaptParallelEvidence(parallelData: { evidence: ParallelEvidence[] }): Evidence[] {
  if (!parallelData || !parallelData.evidence) {
    console.error('Invalid data structure: missing evidence array');
    return [];
  }
  
  return parallelData.evidence.map((item, index) => {
    try {
      return {
        id: index + 1, // Generate numeric ID
        evidence_id: item.id, // Store original string ID
        description: item.description,
        shift_type: item.shift_type,
        entities: normalizeEntities(item.entities || []),
        locations: item.locations || [],
        dates: null, // Not in new schema
        confidence: item.confidence?.score || null,
        implementation_stage: item.implementation_stage || null,
        evidence_nature: item.evidence_nature || null,
        source_reference: item.source_reference || null,
        data_gaps: item.data_gaps || null,
        requires_clarification: item.requires_clarification || null,
        temporal_status: item.status || null,
        sharepoint_link: processSharePointLink(item.sharepoint_link),
        accuracy_notes: item.accuracy_notes || null,
        reporting_period: item.reporting_period ? {
          label: item.reporting_period,
          value: item.reporting_period
        } : null,
        context: item.context ? {
          background: item.context.background || null,
          future_implications: item.context.future_implications || null,
          immediate_impact: item.context.immediate_impact || null,
          stakeholders: item.context.stakeholders || null,
          systems_affected: item.context.systems_affected || null
        } : null,
        supporting_details: item.supporting_details || null,
        created_at: new Date().toISOString(), // Default value
        updated_at: new Date().toISOString()
      };
    } catch (error) {
      console.error(`Error processing evidence item at index ${index}:`, error, item);
      // Return a minimal valid evidence object
      return {
        id: index + 1,
        evidence_id: item.id || `error_${index}`,
        description: item.description || 'Error loading evidence',
        shift_type: item.shift_type || 'unknown',
        entities: [],
        confidence: null,
        context: null,
        created_at: new Date().toISOString()
      } as Evidence;
    }
  });
}